import { useState, useRef } from 'react';
import { Button } from '@tkpod/ui';
import { getCsrfToken } from '@/lib/csrf';

interface ImageUploadInputProps {
  label: string;
  value: string;
  directory?: string;
  onChange: (url: string) => void;
  accept?: string;
  previewSize?: 'sm' | 'md' | 'lg';
}

const PREVIEW_SIZES = {
  sm: 'h-10 w-10',
  md: 'h-16 w-16',
  lg: 'h-24 w-24',
} as const;

export function ImageUploadInput({
  label,
  value,
  directory = 'catalog',
  onChange,
  accept = 'image/png,image/jpeg,image/webp,image/svg+xml',
  previewSize = 'md',
}: ImageUploadInputProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('directory', directory);

      const baseUrl = import.meta.env.VITE_API_URL as string;
      const headers: Record<string, string> = {};
      const csrfToken = getCsrfToken();
      if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

      const res = await fetch(`${baseUrl}/api/v1/admin/upload`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Upload failed' }));
        throw new Error(err.message || 'Upload failed');
      }

      const json = await res.json();
      onChange(json.data?.url ?? '');
    } catch (err) {
      if (err instanceof TypeError && (err.message === 'Failed to fetch' || err.message.includes('NetworkError'))) {
        setError('Could not reach the server. Please check if the API is running.');
      } else {
        const msg = err instanceof Error ? err.message : 'Upload failed';
        setError(msg === 'An unexpected error occurred' ? 'Upload failed. Please check the server logs.' : msg);
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  };

  const handleRemove = () => {
    onChange('');
    setError(null);
  };

  return (
    <div>
      <label className="text-xs font-medium text-text-secondary">{label}</label>
      <div className="mt-1 flex items-start gap-3">
        {value ? (
          <div className="relative group">
            <img
              src={value}
              alt={label}
              className={`${PREVIEW_SIZES[previewSize]} rounded border border-border object-cover`}
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.style.display = 'none';
                img.alt = 'Failed to load';
              }}
            />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-danger-600 text-[10px] text-white opacity-0 transition-opacity group-hover:opacity-100"
              title="Remove image"
            >
              &times;
            </button>
          </div>
        ) : (
          <div className={`${PREVIEW_SIZES[previewSize]} flex items-center justify-center rounded border border-dashed border-border bg-bg-secondary text-text-tertiary`}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
              <path fillRule="evenodd" d="M1 5.25A2.25 2.25 0 013.25 3h13.5A2.25 2.25 0 0119 5.25v9.5A2.25 2.25 0 0116.75 17H3.25A2.25 2.25 0 011 14.75v-9.5zm1.5 5.81V14.75c0 .414.336.75.75.75h13.5a.75.75 0 00.75-.75v-2.06l-2.72-2.72a.75.75 0 00-1.06 0L9.31 14.31a.75.75 0 01-1.06 0l-2.56-2.56a.75.75 0 00-1.06 0L2.5 11.06zM12 7a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleFileChange}
            className="hidden"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? 'Uploading...' : value ? 'Change' : 'Upload'}
          </Button>
          {error && <p className="text-xs text-danger-600">{error}</p>}
        </div>
      </div>
    </div>
  );
}
